import { useCallback, useEffect, useRef } from 'react';
import { DefaultValues, FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { OrganizationDTO, TalentCreateDTO, useGetOrganizationBySlug } from '@/api/generated';
import {
  getBasicInfoSchemaForCreate,
  getBasicInfoSchemaForEdit,
  TalentBasicInfoViewType
} from '@/views/TalentEditInfoModal/constants';

import { yupValidationResolver } from './useYupValidationResolver';

const useTalentBasicInfoForm = <T extends FieldValues>({
  defaultValues,
  viewType
}: {
  defaultValues?: DefaultValues<TalentCreateDTO & T>;
  viewType?: TalentBasicInfoViewType;
} = {}) => {
  const { t } = useTranslation(['validation']);
  const orgDataRef = useRef<OrganizationDTO | undefined>();
  const resolver = useCallback(
    async (data: TalentCreateDTO & T): ReturnType<ReturnType<typeof yupValidationResolver<typeof data>>> =>
      yupValidationResolver(
        viewType === TalentBasicInfoViewType.CREATE
          ? getBasicInfoSchemaForCreate(t, orgDataRef.current)
          : getBasicInfoSchemaForEdit(t, orgDataRef.current)
      )(
        data as TalentCreateDTO &
          Required<Pick<TalentCreateDTO, 'firstName' | 'lastName' | 'organizationSlug' | 'email'>>
      ),
    [viewType, t]
  );

  const formMethods = useForm<T & TalentCreateDTO>({
    defaultValues,
    resolver
  });

  const basicInfo = formMethods.watch();
  const { data: org } = useGetOrganizationBySlug(
    basicInfo.organizationSlug!,
    { isFull: true },
    { query: { enabled: !!basicInfo.organizationSlug } }
  );

  useEffect(() => {
    orgDataRef.current = org;
  }, [org]);

  return { formMethods };
};

export default useTalentBasicInfoForm;
